import React, { useEffect, useMemo, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import LogoUploader from '../components/LogoUploader';
import { drawPdfLogo } from '../utils/logoUpload';
import { applyMobileInputHints } from '../utils/formEnhancements';

const STATUSES = ['Draft', 'Sent', 'Accepted', 'Rejected', 'Expired'];

const TEMPLATES = [
  { id: 'classic', label: 'Classic A4', accent: '#1d4ed8', format: 'a4' },
  { id: 'modern', label: 'Modern Sidebar', accent: '#7c3aed', format: 'a4' },
  { id: 'minimal', label: 'Minimal Mono', accent: '#111827', format: 'a4' },
  { id: 'banner', label: 'Bold Banner', accent: '#0f766e', format: 'a4' },
  { id: 'thermal', label: 'Quick Quote 80mm', accent: '#16a34a', format: [80, 297] },
];

const hexToRgbArr = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const pad2 = (n) => String(n).padStart(2, '0');
const toDMY = (d) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
const todayDMY = () => toDMY(new Date());
const addDaysDMY = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toDMY(d);
};
const genQuoteNo = () => {
  const year = new Date().getFullYear();
  const seq = Math.floor(1000 + Math.random() * 9000);
  return `QT-${year}-${seq}`;
};

const formatINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const newItem = (description = '', qty = 1, price = 0, gst = 18, discount = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  description,
  qty,
  price,
  gst,
  discount, // per-item discount %
});

const QuotationGenerator = () => {
  const [template, setTemplate] = useState('classic');
  const [isCompactViewport, setIsCompactViewport] = useState(false);
  const pageRef = useRef(null);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsCompactViewport(window.innerWidth <= 768);
    };

    updateViewportMode();
    window.addEventListener('resize', updateViewportMode);

    return () => {
      window.removeEventListener('resize', updateViewportMode);
    };
  }, []);

  useEffect(() => {
    applyMobileInputHints(pageRef.current);
  }, [items.length, client.phone, company.phone, charges]);

  const [company, setCompany] = useState({
    name: 'BrightWave Technologies Pvt Ltd',
    address: 'Plot 42, HiTech City, Hyderabad 500081',
    phone: '+91 98765 43210',
    email: 'sales@brightwave.io',
    website: 'www.brightwave.io',
    gstin: '36ABCDE1234F1Z5',
    pan: 'ABCDE1234F',
  });

  const [client, setClient] = useState({
    name: 'Acme Retail India Pvt Ltd',
    contactPerson: 'Mr. Karthik Iyer',
    address: '88 MG Road, Bengaluru 560001',
    phone: '+91 90000 11122',
    email: 'karthik@acmeretail.in',
    gstin: '29AAAAA0000A1Z1',
  });

  const [meta, setMeta] = useState({
    quoteNo: genQuoteNo(),
    subject: 'Quotation for Web Application Development & Annual Maintenance',
    issueDate: todayDMY(),
    validUntil: addDaysDMY(30),
    status: 'Sent',
    reference: 'RFQ/2026/4421',
    preparedBy: 'Anita Rao',
  });

  const [items, setItems] = useState([
    newItem('Web Application Development (Phase 1)', 1, 250000, 18, 0),
    newItem('UI/UX Design & Prototyping', 1, 65000, 18, 5),
    newItem('Cloud Hosting Setup (1 year)', 1, 24000, 18, 0),
    newItem('Annual Maintenance Contract', 12, 8500, 18, 0),
  ]);

  const [charges, setCharges] = useState({
    shipping: 0,
    installation: 0,
    discountPercent: 0, // overall additional
    advancePercent: 30, // requested advance
    roundOff: true,
  });

  const [notes, setNotes] = useState(
    'Thank you for the opportunity. We are pleased to submit the following quotation for your kind consideration. Please feel free to reach out for any clarifications.'
  );

  const [terms, setTerms] = useState(
    '1. Prices are valid for 30 days from the date of issue.\n2. Payment terms: 30% advance, 40% on UAT, 30% on go-live.\n3. Taxes (GST) as applicable.\n4. Any change in scope will be billed separately.\n5. Delivery timelines start from the date of advance receipt.'
  );

  const [bank, setBank] = useState({
    accountName: 'BrightWave Technologies Pvt Ltd',
    accountNo: '5012 3344 5566',
    bank: 'HDFC Bank, Hitech City Branch',
    ifsc: 'HDFC0001234',
    upi: 'brightwave@hdfcbank',
  });

  const [logo, setLogo] = useState(null);
  const accent = TEMPLATES.find((t) => t.id === template)?.accent || '#1d4ed8';

  // ---------- COMPUTATIONS ----------
  const totals = useMemo(() => {
    let subtotal = 0;
    let itemDiscount = 0;
    let totalGst = 0;
    items.forEach((it) => {
      const q = Number(it.qty) || 0;
      const p = Number(it.price) || 0;
      const line = q * p;
      const dPct = Number(it.discount) || 0;
      const lineDisc = (line * dPct) / 100;
      const taxable = line - lineDisc;
      const gst = (taxable * (Number(it.gst) || 0)) / 100;
      subtotal += line;
      itemDiscount += lineDisc;
      totalGst += gst;
    });
    const taxableTotal = subtotal - itemDiscount;
    const overallDisc = (taxableTotal * (Number(charges.discountPercent) || 0)) / 100;
    const afterOverall = taxableTotal - overallDisc;
    const cgst = totalGst / 2;
    const sgst = totalGst / 2;
    const shipping = Number(charges.shipping) || 0;
    const installation = Number(charges.installation) || 0;
    const grand = afterOverall + totalGst + shipping + installation;
    const grandRounded = Math.round(grand);
    const advance = (grand * (Number(charges.advancePercent) || 0)) / 100;
    return {
      subtotal,
      itemDiscount,
      taxableTotal,
      overallDisc,
      afterOverall,
      totalGst,
      cgst,
      sgst,
      shipping,
      installation,
      grand: Math.round(grand * 100) / 100,
      grandRounded,
      roundOff: grandRounded - grand,
      advance: Math.round(advance * 100) / 100,
    };
  }, [items, charges]);

  const finalAmount = charges.roundOff ? totals.grandRounded : totals.grand;
  const balanceAfterAdvance = Math.max(0, finalAmount - totals.advance);

  const updateItem = (id, patch) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, newItem()]);
  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const handleReset = () => {
    setCompany({ name: '', address: '', phone: '', email: '', website: '', gstin: '', pan: '' });
    setClient({ name: '', contactPerson: '', address: '', phone: '', email: '', gstin: '' });
    setMeta({
      quoteNo: genQuoteNo(),
      subject: '',
      issueDate: todayDMY(),
      validUntil: addDaysDMY(30),
      status: 'Draft',
      reference: '',
      preparedBy: '',
    });
    setItems([newItem()]);
    setCharges({ shipping: 0, installation: 0, discountPercent: 0, advancePercent: 0, roundOff: true });
    setNotes('');
    setTerms('');
    setBank({ accountName: '', accountNo: '', bank: '', ifsc: '', upi: '' });
  };

  // ---------- PDF HELPERS ----------
  const drawCompanyHeader = (doc, color, left, right, withDivider = true) => {
    let y = 16;
    if (logo) {
      drawPdfLogo(doc, logo, left, 10, 22, company.name, [240, 240, 240]);
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...color);
    doc.text(company.name || 'Company', logo ? left + 26 : left, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    if (company.address) {
      const lines = doc.splitTextToSize(company.address, 110);
      lines.forEach((ln) => { doc.text(ln, logo ? left + 26 : left, y); y += 4; });
    }
    const line2 = [company.phone, company.email, company.website].filter(Boolean).join('  ·  ');
    if (line2) { doc.text(line2, logo ? left + 26 : left, y); y += 4; }
    if (company.gstin) { doc.text(`GSTIN: ${company.gstin}${company.pan ? `   PAN: ${company.pan}` : ''}`, logo ? left + 26 : left, y); y += 4; }
    if (withDivider) {
      y += 2;
      doc.setDrawColor(...color);
      doc.setLineWidth(0.4);
      doc.line(left, y, right, y);
      y += 2;
    }
    return y;
  };

  const drawItemsTable = (doc, startY, left, right, color) => {
    let y = startY;
    doc.setFillColor(...color);
    doc.rect(left, y, right - left, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('#', left + 3, y + 6);
    doc.text('Description', left + 10, y + 6);
    doc.text('Qty', right - 78, y + 6, { align: 'right' });
    doc.text('Rate', right - 56, y + 6, { align: 'right' });
    doc.text('Disc%', right - 36, y + 6, { align: 'right' });
    doc.text('GST%', right - 20, y + 6, { align: 'right' });
    doc.text('Amount', right - 3, y + 6, { align: 'right' });
    y += 9;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    items.forEach((it, idx) => {
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      const line = qty * price;
      const disc = (line * (Number(it.discount) || 0)) / 100;
      const amt = line - disc;
      const nameLines = doc.splitTextToSize(it.description || '-', right - left - 100);
      const rowH = Math.max(7, nameLines.length * 4.5 + 2);
      doc.rect(left, y, right - left, rowH);
      doc.text(String(idx + 1), left + 3, y + 5);
      doc.text(nameLines, left + 10, y + 5);
      doc.text(String(qty), right - 78, y + 5, { align: 'right' });
      doc.text(price.toFixed(2), right - 56, y + 5, { align: 'right' });
      doc.text(String(it.discount || 0), right - 36, y + 5, { align: 'right' });
      doc.text(String(it.gst || 0), right - 20, y + 5, { align: 'right' });
      doc.text(amt.toFixed(2), right - 3, y + 5, { align: 'right' });
      y += rowH;
    });
    return y;
  };

  const drawTotalsBlock = (doc, startY, left, right, color, includeAdvance = true) => {
    let y = startY + 4;
    const totalsX = right - 90;
    const row = (label, value, bold = false, big = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(big ? 13 : 10);
      doc.text(label, totalsX, y);
      doc.text(value, right, y, { align: 'right' });
      y += big ? 8 : 5;
    };
    row('Subtotal', formatINR(totals.subtotal));
    if (totals.itemDiscount > 0) row('Item Discount', `- ${formatINR(totals.itemDiscount)}`);
    if (totals.overallDisc > 0) row(`Overall Discount (${charges.discountPercent}%)`, `- ${formatINR(totals.overallDisc)}`);
    row('Taxable Value', formatINR(totals.afterOverall));
    if (totals.cgst > 0) row('CGST', formatINR(totals.cgst));
    if (totals.sgst > 0) row('SGST', formatINR(totals.sgst));
    if (totals.shipping > 0) row('Shipping', formatINR(totals.shipping));
    if (totals.installation > 0) row('Installation', formatINR(totals.installation));
    if (charges.roundOff && Math.abs(totals.roundOff) > 0.001)
      row('Round Off', totals.roundOff >= 0 ? `+ ${formatINR(totals.roundOff)}` : `- ${formatINR(Math.abs(totals.roundOff))}`);
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.5);
    doc.line(totalsX, y, right, y);
    y += 6;
    doc.setTextColor(...color);
    row('GRAND TOTAL', `Rs. ${formatINR(finalAmount)}`, true, true);
    doc.setTextColor(0, 0, 0);
    if (includeAdvance && totals.advance > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(`Advance Requested (${charges.advancePercent}%):`, totalsX, y);
      doc.text(`Rs. ${formatINR(totals.advance)}`, right, y, { align: 'right' });
      y += 5;
      doc.text('Balance on Delivery:', totalsX, y);
      doc.text(`Rs. ${formatINR(balanceAfterAdvance)}`, right, y, { align: 'right' });
      y += 5;
      doc.setTextColor(0, 0, 0);
    }
    return y;
  };

  const drawNotesAndTerms = (doc, startY, left, right, color) => {
    let y = startY + 4;
    const w = right - left;
    if (notes) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...color);
      doc.text('Notes', left, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(40, 40, 40);
      const lines = doc.splitTextToSize(notes, w);
      lines.forEach((ln) => { doc.text(ln, left, y); y += 4; });
      y += 2;
    }
    if (terms) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...color);
      doc.text('Terms & Conditions', left, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(40, 40, 40);
      terms.split('\n').forEach((row) => {
        const lines = doc.splitTextToSize(row, w);
        lines.forEach((ln) => { doc.text(ln, left, y); y += 4; });
      });
      y += 2;
    }
    const hasBank = bank.accountNo || bank.upi;
    if (hasBank) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...color);
      doc.text('Payment Details', left, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(40, 40, 40);
      if (bank.accountName) { doc.text(`A/c Name: ${bank.accountName}`, left, y); y += 4; }
      if (bank.accountNo) { doc.text(`A/c No: ${bank.accountNo}`, left, y); y += 4; }
      if (bank.bank) { doc.text(`Bank: ${bank.bank}`, left, y); y += 4; }
      if (bank.ifsc) { doc.text(`IFSC: ${bank.ifsc}`, left, y); y += 4; }
      if (bank.upi) { doc.text(`UPI: ${bank.upi}`, left, y); y += 4; }
    }
    doc.setTextColor(0, 0, 0);
    return y;
  };

  // 1. CLASSIC A4
  const renderClassic = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = hexToRgbArr(accent);

    let y = drawCompanyHeader(doc, color, left, right);
    y += 4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(...color);
    doc.text('QUOTATION', right, y, { align: 'right' });
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'normal');
    doc.text(`No: ${meta.quoteNo}`, right, y + 5, { align: 'right' });
    doc.text(`Date: ${meta.issueDate}`, right, y + 10, { align: 'right' });
    doc.text(`Valid Until: ${meta.validUntil}`, right, y + 15, { align: 'right' });

    // Client block on left
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...color);
    doc.text('Quotation For', left, y);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(client.name || '—', left, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    let cy = y + 11;
    if (client.contactPerson) { doc.text(`Attn: ${client.contactPerson}`, left, cy); cy += 4; }
    if (client.address) {
      const lines = doc.splitTextToSize(client.address, 90);
      lines.forEach((ln) => { doc.text(ln, left, cy); cy += 4; });
    }
    if (client.phone) { doc.text(`Tel: ${client.phone}`, left, cy); cy += 4; }
    if (client.email) { doc.text(`Email: ${client.email}`, left, cy); cy += 4; }
    if (client.gstin) { doc.text(`GSTIN: ${client.gstin}`, left, cy); cy += 4; }

    y = Math.max(y + 20, cy) + 4;

    if (meta.subject) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...color);
      doc.text('Subject:', left, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const subLines = doc.splitTextToSize(meta.subject, right - left - 22);
      subLines.forEach((ln, i) => doc.text(ln, left + 22, y + i * 4));
      y += Math.max(5, subLines.length * 4) + 4;
    }

    y = drawItemsTable(doc, y, left, right, color);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotesAndTerms(doc, y, left, right, color);

    // Signature
    y = Math.min(y + 6, 270);
    doc.setDrawColor(120, 120, 120);
    doc.line(right - 60, y, right, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`For ${company.name || 'Company'}`, right - 30, y + 5, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    if (meta.preparedBy) doc.text(`Prepared by: ${meta.preparedBy}`, right - 30, y + 10, { align: 'center' });

    return doc;
  };

  // 2. MODERN SIDEBAR
  const renderModern = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210, h = 297;
    const sideW = 60;
    const color = hexToRgbArr(accent);

    doc.setFillColor(...color);
    doc.rect(0, 0, sideW, h, 'F');
    if (logo) drawPdfLogo(doc, logo, (sideW - 30) / 2, 14, 30, company.name, [255, 255, 255]);

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    const titleY = logo ? 50 : 22;
    const nameLines = doc.splitTextToSize(company.name || 'Company', sideW - 10);
    nameLines.forEach((ln, i) => doc.text(ln, sideW / 2, titleY + i * 6, { align: 'center' }));
    let sy = titleY + nameLines.length * 6 + 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (company.address) {
      const lines = doc.splitTextToSize(company.address, sideW - 10);
      lines.forEach((ln) => { doc.text(ln, sideW / 2, sy, { align: 'center' }); sy += 3.5; });
    }
    if (company.phone) { doc.text(company.phone, sideW / 2, sy, { align: 'center' }); sy += 3.5; }
    if (company.email) {
      const eLines = doc.splitTextToSize(company.email, sideW - 10);
      eLines.forEach((ln) => { doc.text(ln, sideW / 2, sy, { align: 'center' }); sy += 3.5; });
    }
    if (company.website) { doc.text(company.website, sideW / 2, sy, { align: 'center' }); sy += 3.5; }
    sy += 4;
    doc.setDrawColor(255, 255, 255);
    doc.line(8, sy, sideW - 8, sy);
    sy += 5;
    if (company.gstin) { doc.setFont('helvetica', 'bold'); doc.text('GSTIN', sideW / 2, sy, { align: 'center' }); sy += 3.5; doc.setFont('helvetica', 'normal'); doc.text(company.gstin, sideW / 2, sy, { align: 'center' }); sy += 5; }
    if (company.pan) { doc.setFont('helvetica', 'bold'); doc.text('PAN', sideW / 2, sy, { align: 'center' }); sy += 3.5; doc.setFont('helvetica', 'normal'); doc.text(company.pan, sideW / 2, sy, { align: 'center' }); sy += 5; }

    // Right pane
    const left = sideW + 10;
    const right = w - 14;
    let y = 18;
    doc.setTextColor(...color);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.text('QUOTATION', left, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`#${meta.quoteNo}   ·   ${meta.status}`, left, y);
    y += 8;

    // Client + dates
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('To', left, y);
    doc.text('Issue Date', (left + right) / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(client.name || '—', left, y);
    doc.text(meta.issueDate, (left + right) / 2, y);
    y += 5;
    if (client.contactPerson) doc.text(`Attn: ${client.contactPerson}`, left, y);
    doc.setFont('helvetica', 'bold');
    doc.text('Valid Until', (left + right) / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    if (client.address) {
      const lines = doc.splitTextToSize(client.address, (right - left) / 2 - 5);
      lines.forEach((ln) => { doc.text(ln, left, y); y += 4; });
    }
    doc.text(meta.validUntil, (left + right) / 2, y - (client.address ? 4 : 0));
    y += 4;
    if (client.gstin) { doc.text(`GSTIN: ${client.gstin}`, left, y); y += 4; }

    y += 4;
    if (meta.subject) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...color);
      doc.text('Subject', left, y); y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const subLines = doc.splitTextToSize(meta.subject, right - left);
      subLines.forEach((ln) => { doc.text(ln, left, y); y += 4; });
      y += 2;
    }

    y = drawItemsTable(doc, y, left, right, color);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotesAndTerms(doc, y, left, right, color);

    return doc;
  };

  // 3. MINIMAL MONO A4
  const renderMinimal = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = [40, 40, 40];

    let y = 18;
    if (logo) {
      drawPdfLogo(doc, logo, left, 12, 18, company.name, [240, 240, 240]);
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(company.name || 'Company', logo ? left + 22 : left, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    const headLine = [company.address, company.phone, company.email].filter(Boolean).join('  ·  ');
    if (headLine) {
      const lines = doc.splitTextToSize(headLine, right - (logo ? left + 22 : left));
      lines.forEach((ln) => { doc.text(ln, logo ? left + 22 : left, y); y += 4; });
    }
    if (company.gstin) { doc.text(`GSTIN ${company.gstin}`, logo ? left + 22 : left, y); y += 4; }

    y += 4;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.6);
    doc.line(left, y, right, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(0, 0, 0);
    doc.text('QUOTATION', left, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(meta.quoteNo, right, y, { align: 'right' });
    y += 8;

    doc.setFontSize(9);
    doc.text(`Date: ${meta.issueDate}    Valid Until: ${meta.validUntil}    Status: ${meta.status}`, left, y);
    y += 8;

    // Client
    doc.setFont('helvetica', 'bold');
    doc.text('To:', left, y);
    doc.setFont('helvetica', 'normal');
    doc.text(client.name || '—', left + 10, y);
    y += 5;
    if (client.address) {
      const lines = doc.splitTextToSize(client.address, right - left - 10);
      lines.forEach((ln) => { doc.text(ln, left + 10, y); y += 4; });
    }
    if (client.phone || client.email) {
      doc.text([client.phone, client.email].filter(Boolean).join('  ·  '), left + 10, y);
      y += 4;
    }
    if (client.gstin) { doc.text(`GSTIN: ${client.gstin}`, left + 10, y); y += 4; }

    y += 3;
    if (meta.subject) {
      doc.setFont('helvetica', 'bold');
      doc.text('Re:', left, y);
      doc.setFont('helvetica', 'normal');
      const subLines = doc.splitTextToSize(meta.subject, right - left - 10);
      subLines.forEach((ln, i) => doc.text(ln, left + 10, y + i * 4));
      y += Math.max(4, subLines.length * 4) + 4;
    }

    // simple items (mono table, no fill)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('#', left, y);
    doc.text('Description', left + 8, y);
    doc.text('Qty', right - 78, y, { align: 'right' });
    doc.text('Rate', right - 56, y, { align: 'right' });
    doc.text('Disc%', right - 36, y, { align: 'right' });
    doc.text('GST%', right - 20, y, { align: 'right' });
    doc.text('Amount', right - 3, y, { align: 'right' });
    y += 2;
    doc.line(left, y, right, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    items.forEach((it, idx) => {
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      const line = qty * price;
      const disc = (line * (Number(it.discount) || 0)) / 100;
      const amt = line - disc;
      const nameLines = doc.splitTextToSize(it.description || '-', right - left - 100);
      doc.text(String(idx + 1), left, y);
      doc.text(nameLines, left + 8, y);
      doc.text(String(qty), right - 78, y, { align: 'right' });
      doc.text(price.toFixed(2), right - 56, y, { align: 'right' });
      doc.text(String(it.discount || 0), right - 36, y, { align: 'right' });
      doc.text(String(it.gst || 0), right - 20, y, { align: 'right' });
      doc.text(amt.toFixed(2), right - 3, y, { align: 'right' });
      y += Math.max(5, nameLines.length * 4.5);
    });
    doc.line(left, y, right, y);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotesAndTerms(doc, y, left, right, color);
    return doc;
  };

  // 4. BANNER A4
  const renderBanner = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = hexToRgbArr(accent);

    doc.setFillColor(...color);
    doc.rect(0, 0, w, 38, 'F');
    if (logo) drawPdfLogo(doc, logo, left, 6, 26, company.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(company.name || 'Company', logo ? left + 30 : left, 18);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('QUOTATION', logo ? left + 30 : left, 26);
    doc.setFontSize(9);
    const subline = [company.address, company.phone, company.email].filter(Boolean).join('  ·  ');
    if (subline) {
      const lines = doc.splitTextToSize(subline, w - (logo ? left + 30 : left) - 14);
      lines.forEach((ln, i) => doc.text(ln, logo ? left + 30 : left, 32 + i * 4));
    }
    doc.text(`#${meta.quoteNo}`, right, 14, { align: 'right' });
    doc.text(meta.issueDate, right, 20, { align: 'right' });
    doc.text(`Valid: ${meta.validUntil}`, right, 26, { align: 'right' });

    let y = 48;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...color);
    doc.text('Quotation For', left, y);
    doc.text('Status', right - 40, y);
    y += 5;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text(client.name || '—', left, y);
    doc.setFont('helvetica', 'normal');
    doc.text(meta.status, right - 40, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    if (client.contactPerson) { doc.text(`Attn: ${client.contactPerson}`, left, y); y += 4; }
    if (client.address) {
      const lines = doc.splitTextToSize(client.address, (right - left) / 2);
      lines.forEach((ln) => { doc.text(ln, left, y); y += 4; });
    }
    if (client.gstin) { doc.text(`GSTIN: ${client.gstin}`, left, y); y += 4; }

    y += 3;
    if (meta.subject) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setFillColor(245, 245, 250);
      doc.rect(left, y - 4, right - left, 8, 'F');
      doc.setTextColor(...color);
      doc.text('Subject:', left + 2, y + 1);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const subLines = doc.splitTextToSize(meta.subject, right - left - 22);
      subLines.forEach((ln, i) => doc.text(ln, left + 22, y + 1 + i * 4));
      y += Math.max(8, subLines.length * 4 + 4);
    }

    y = drawItemsTable(doc, y, left, right, color);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotesAndTerms(doc, y, left, right, color);

    return doc;
  };

  // 5. THERMAL QUICK QUOTE 80mm
  const renderThermal = () => {
    const w = 80, h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const color = hexToRgbArr(accent);
    let y = 8;
    if (logo) {
      drawPdfLogo(doc, logo, (w - 16) / 2, 4, 16, company.name, [240, 240, 240]);
      y = 22;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...color);
    doc.text(company.name || 'Company', w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(0, 0, 0);
    if (company.address) {
      const lines = doc.splitTextToSize(company.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3; });
    }
    if (company.phone) { doc.text(company.phone, w / 2, y, { align: 'center' }); y += 3; }
    if (company.gstin) { doc.text(`GSTIN ${company.gstin}`, w / 2, y, { align: 'center' }); y += 3; }
    y += 1;
    doc.setDrawColor(0, 0, 0);
    doc.line(5, y, w - 5, y);
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('QUOTATION', w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`#${meta.quoteNo}`, 5, y);
    doc.text(meta.issueDate, w - 5, y, { align: 'right' });
    y += 4;
    doc.text(`Valid: ${meta.validUntil}`, 5, y);
    y += 4;
    if (client.name) { doc.text(`To: ${client.name}`, 5, y); y += 4; }
    if (meta.subject) {
      const subLines = doc.splitTextToSize(meta.subject, w - 10);
      doc.setFont('helvetica', 'italic');
      subLines.forEach((ln) => { doc.text(ln, 5, y); y += 3.5; });
      doc.setFont('helvetica', 'normal');
    }
    y += 1;
    doc.line(5, y, w - 5, y);
    y += 4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('Item', 5, y);
    doc.text('Qty', 40, y, { align: 'right' });
    doc.text('Rate', 56, y, { align: 'right' });
    doc.text('Amt', w - 5, y, { align: 'right' });
    y += 2;
    doc.line(5, y, w - 5, y);
    y += 3;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    items.forEach((it) => {
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      const line = qty * price;
      const disc = (line * (Number(it.discount) || 0)) / 100;
      const amt = line - disc;
      const nameLines = doc.splitTextToSize(it.description || '-', 32);
      doc.text(nameLines, 5, y);
      doc.text(String(qty), 40, y, { align: 'right' });
      doc.text(price.toFixed(2), 56, y, { align: 'right' });
      doc.text(amt.toFixed(2), w - 5, y, { align: 'right' });
      y += Math.max(4, nameLines.length * 3.5);
    });
    y += 1;
    doc.line(5, y, w - 5, y);
    y += 4;

    const row = (label, value, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(bold ? 11 : 8);
      doc.text(label, 5, y);
      doc.text(value, w - 5, y, { align: 'right' });
      y += bold ? 6 : 4;
    };
    row('Subtotal', formatINR(totals.subtotal));
    if (totals.itemDiscount > 0) row('Disc', `- ${formatINR(totals.itemDiscount)}`);
    if (totals.totalGst > 0) row('GST', formatINR(totals.totalGst));
    if (totals.shipping > 0) row('Shipping', formatINR(totals.shipping));
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.line(5, y, w - 5, y);
    y += 5;
    doc.setTextColor(...color);
    row('TOTAL', `Rs. ${formatINR(finalAmount)}`, true);
    doc.setTextColor(0, 0, 0);
    if (totals.advance > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Advance (${charges.advancePercent}%): Rs.${formatINR(totals.advance)}`, w / 2, y, { align: 'center' });
      y += 4;
    }
    y += 2;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text(`Status: ${meta.status}`, w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...color);
    doc.text('Thank you!', w / 2, y, { align: 'center' });
    return doc;
  };

  const TEMPLATE_RENDERERS = {
    classic: renderClassic,
    modern: renderModern,
    minimal: renderMinimal,
    banner: renderBanner,
    thermal: renderThermal,
  };

  const buildPDF = () => (TEMPLATE_RENDERERS[template] || renderClassic)();

  const handleDownloadPDF = () => {
    const doc = buildPDF();
    doc.save(`Quotation_${meta.quoteNo || 'document'}.pdf`);
  };
  const handlePrint = () => {
    const doc = buildPDF();
    doc.autoPrint();
    const url = doc.output('bloburl');
    window.open(url, '_blank');
  };

  return (
    <div className="calculator-page fade-in" ref={pageRef}>
      <SEO
        title="Quotation Generator (Free, with GST)"
        description="Free Quotation Generator India - create professional GST quotations / price proposals with company & client details, itemised pricing, discounts, advance %, terms & conditions, and 5 printable templates. Download as PDF."
        keywords="quotation generator, quote maker, price quote pdf, gst quotation format india, sales quotation, quotation template, free quote generator"
        canonicalPath="/quotation"
        calculatorType="QuotationGenerator"
        calculatorDescription="Generate professional Indian GST quotations with itemised pricing, item-wise & overall discounts, CGST/SGST, advance percentage, terms & conditions, bank details and 5 printable templates."
      />

      <div className="calculator-header">
        <h1>Quotation Generator</h1>
        <p>Professional GST quotations with itemised pricing, discounts, advance % and 5 templates</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          {/* Template chips */}
          <div className="input-group">
            <label>Quotation Template</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {TEMPLATES.map((t) => {
                const active = template === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1.5px solid ${active ? t.accent : '#cbd5e1'}`,
                      background: active ? t.accent : '#fff',
                      color: active ? '#fff' : '#0f172a',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: t.accent, border: active ? '1px solid #fff' : 'none' }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <LogoUploader logo={logo} setLogo={setLogo} label="Company Logo" accent={accent} />

          <h3 style={{ marginTop: 12, marginBottom: 12 }}>Your Company (From)</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Company Name</label>
              <input type="text" value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} placeholder="e.g. BrightWave Technologies" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Phone</label>
              <input type="text" value={company.phone} onChange={(e) => setCompany({ ...company, phone: e.target.value })} placeholder="+91 ..." />
            </div>
          </div>
          <div className="input-group">
            <label>Address</label>
            <input type="text" value={company.address} onChange={(e) => setCompany({ ...company, address: e.target.value })} placeholder="Street, City, PIN" />
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Email</label>
              <input type="text" value={company.email} onChange={(e) => setCompany({ ...company, email: e.target.value })} placeholder="sales@..." />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Website</label>
              <input type="text" value={company.website} onChange={(e) => setCompany({ ...company, website: e.target.value })} placeholder="www..." />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>GSTIN</label>
              <input type="text" value={company.gstin} onChange={(e) => setCompany({ ...company, gstin: e.target.value.toUpperCase() })} placeholder="29ABCDE1234F1Z5" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>PAN</label>
              <input type="text" value={company.pan} onChange={(e) => setCompany({ ...company, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Client (Bill To)</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Client Name</label>
              <input type="text" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} placeholder="Customer or Company" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Contact Person</label>
              <input type="text" value={client.contactPerson} onChange={(e) => setClient({ ...client, contactPerson: e.target.value })} placeholder="Mr./Ms." />
            </div>
          </div>
          <div className="input-group">
            <label>Address</label>
            <input type="text" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} placeholder="Street, City, PIN" />
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Phone</label>
              <input type="text" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} placeholder="+91 ..." />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Email</label>
              <input type="text" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} placeholder="client@..." />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>GSTIN (optional)</label>
              <input type="text" value={client.gstin} onChange={(e) => setClient({ ...client, gstin: e.target.value.toUpperCase() })} placeholder="GSTIN" />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Quotation Info</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Quote No</label>
              <input type="text" value={meta.quoteNo} onChange={(e) => setMeta({ ...meta, quoteNo: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Issue Date</label>
              <input type="text" value={meta.issueDate} onChange={(e) => setMeta({ ...meta, issueDate: e.target.value })} placeholder="DD/MM/YYYY" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Valid Until</label>
              <input type="text" value={meta.validUntil} onChange={(e) => setMeta({ ...meta, validUntil: e.target.value })} placeholder="DD/MM/YYYY" />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Status</label>
              <select value={meta.status} onChange={(e) => setMeta({ ...meta, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Reference / RFQ</label>
              <input type="text" value={meta.reference} onChange={(e) => setMeta({ ...meta, reference: e.target.value })} placeholder="RFQ/..." />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Prepared By</label>
              <input type="text" value={meta.preparedBy} onChange={(e) => setMeta({ ...meta, preparedBy: e.target.value })} placeholder="Name" />
            </div>
          </div>
          <div className="input-group">
            <label>Subject</label>
            <input type="text" value={meta.subject} onChange={(e) => setMeta({ ...meta, subject: e.target.value })} placeholder="Quotation for ..." />
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 8 }}>Line Items</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((it, idx) => (
              <div key={it.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, background: '#f8fafc' }}>
                <div className="input-group" style={{ marginBottom: 8 }}>
                  <label>{idx + 1}. Description</label>
                  <input type="text" value={it.description} onChange={(e) => updateItem(it.id, { description: e.target.value })} placeholder="Item / Service description" />
                </div>
                <div className="flex-row" style={{ alignItems: 'flex-end' }}>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Qty</label>
                    <input type="number" min="0" value={it.qty} onChange={(e) => updateItem(it.id, { qty: e.target.value })} />
                  </div>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Rate (₹)</label>
                    <input type="number" min="0" step="0.01" value={it.price} onChange={(e) => updateItem(it.id, { price: e.target.value })} />
                  </div>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Discount %</label>
                    <input type="number" min="0" max="100" step="0.01" value={it.discount} onChange={(e) => updateItem(it.id, { discount: e.target.value })} />
                  </div>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>GST %</label>
                    <input type="number" min="0" step="0.01" value={it.gst} onChange={(e) => updateItem(it.id, { gst: e.target.value })} />
                  </div>
                  <button type="button" onClick={() => removeItem(it.id)} className="btn btn-outline" style={{ padding: '8px 10px', marginBottom: 0 }} title="Remove">
                    <FaTrash />
                  </button>
                </div>
                <div style={{ marginTop: 6, fontSize: 12, color: '#475569', textAlign: 'right' }}>
                  Line Total:{' '}
                  <strong>
                    ₹ {formatINR(
                      (Number(it.qty) || 0) * (Number(it.price) || 0) *
                      (1 - (Number(it.discount) || 0) / 100)
                    )}
                  </strong>
                </div>
              </div>
            ))}
          </div>
          <button type="button" onClick={addItem} className="btn btn-outline" style={{ marginTop: 10 }}>
            <FaPlus /> Add Item
          </button>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Charges & Terms</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Overall Discount %</label>
              <input type="number" min="0" max="100" step="0.01" value={charges.discountPercent} onChange={(e) => setCharges({ ...charges, discountPercent: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Shipping (₹)</label>
              <input type="number" min="0" step="0.01" value={charges.shipping} onChange={(e) => setCharges({ ...charges, shipping: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Installation (₹)</label>
              <input type="number" min="0" step="0.01" value={charges.installation} onChange={(e) => setCharges({ ...charges, installation: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Advance %</label>
              <input type="number" min="0" max="100" step="0.01" value={charges.advancePercent} onChange={(e) => setCharges({ ...charges, advancePercent: e.target.value })} />
            </div>
          </div>
          <div className="input-group">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={charges.roundOff} onChange={(e) => setCharges({ ...charges, roundOff: e.target.checked })} />
              Round off grand total to nearest rupee
            </label>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              GST is split as CGST/SGST. Use item-wise GST % per line.
            </div>
          </div>

          <div className="input-group">
            <label>Notes / Cover Message</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional cover message shown above terms"
              style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit', fontSize: 13 }}
            />
          </div>
          <div className="input-group">
            <label>Terms & Conditions</label>
            <textarea
              rows={5}
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              placeholder="Each line becomes a separate paragraph"
              style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit', fontSize: 13 }}
            />
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Bank / Payment Details (optional)</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Account Name</label>
              <input type="text" value={bank.accountName} onChange={(e) => setBank({ ...bank, accountName: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Account No</label>
              <input type="text" value={bank.accountNo} onChange={(e) => setBank({ ...bank, accountNo: e.target.value })} />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Bank & Branch</label>
              <input type="text" value={bank.bank} onChange={(e) => setBank({ ...bank, bank: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>IFSC</label>
              <input type="text" value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>UPI ID</label>
              <input type="text" value={bank.upi} onChange={(e) => setBank({ ...bank, upi: e.target.value })} placeholder="name@bank" />
            </div>
          </div>

          <div className="button-group" style={{ marginTop: 16, display: 'flex', flexDirection: isCompactViewport ? 'column' : 'row', gap: 10 }}>
            <button type="button" onClick={handleDownloadPDF} className="btn btn-primary">
              <FaDownload /> Download PDF
            </button>
            <button type="button" onClick={handlePrint} className="btn btn-outline">
              <FaPrint /> Print
            </button>
            <button type="button" onClick={handleReset} className="btn btn-outline">
              <FaRedo /> Reset
            </button>
          </div>
        </div>

        <div className="calculator-results">
          <div style={{ marginBottom: 12, fontSize: 13, color: '#475569' }}>
            Live preview · Template: <strong>{TEMPLATES.find((t) => t.id === template)?.label}</strong>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 16px rgba(15,23,42,0.08)', overflow: 'hidden' }}>
            <QuotationPreview
              template={template}
              accent={accent}
              company={company}
              client={client}
              meta={meta}
              items={items}
              totals={totals}
              charges={charges}
              notes={notes}
              terms={terms}
              bank={bank}
              finalAmount={finalAmount}
              balanceAfterAdvance={balanceAfterAdvance}
              logo={logo}
              isCompactViewport={isCompactViewport}
            />
          </div>
          <div style={{ marginTop: 12, padding: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, color: '#1e3a8a' }}>
            <strong>Grand Total:</strong> ₹ {formatINR(finalAmount)}
            {totals.totalGst > 0 && <> · GST: ₹ {formatINR(totals.totalGst)}</>}
            {totals.advance > 0 && <> · Advance ({charges.advancePercent}%): ₹ {formatINR(totals.advance)}</>}
            {totals.advance > 0 && <> · Balance: ₹ {formatINR(balanceAfterAdvance)}</>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- LIVE PREVIEW ----------
const previewBase = {
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  color: '#0f172a',
};

const LogoImg = ({ logo, style }) =>
  logo ? <img src={logo} alt="logo" style={{ objectFit: 'contain', ...style }} /> : null;

const QuotationPreview = ({ template, accent, company, client, meta, items, totals, charges, notes, terms, bank, finalAmount, balanceAfterAdvance, logo, isCompactViewport }) => {
  const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const ItemsTable = ({ compact = false }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? 11 : 12 }}>
      <thead>
        <tr style={{ background: '#f1f5f9' }}>
          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>Description</th>
          <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>Qty</th>
          <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>Rate</th>
          {!compact && <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>Disc%</th>}
          {!compact && <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>GST%</th>}
          <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => {
          const qty = Number(it.qty) || 0;
          const price = Number(it.price) || 0;
          const line = qty * price;
          const disc = (line * (Number(it.discount) || 0)) / 100;
          const amt = line - disc;
          return (
            <tr key={it.id} style={{ borderBottom: '1px dashed #e2e8f0' }}>
              <td style={{ padding: '6px 8px' }}>{it.description || '—'}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{qty}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(price)}</td>
              {!compact && <td style={{ padding: '6px 8px', textAlign: 'right' }}>{it.discount || 0}%</td>}
              {!compact && <td style={{ padding: '6px 8px', textAlign: 'right' }}>{it.gst || 0}%</td>}
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(amt)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );

  const TotalsBlock = () => (
    <div style={{ marginLeft: 'auto', marginTop: 12, minWidth: isCompactViewport ? 0 : 260, width: isCompactViewport ? '100%' : 'auto', fontSize: 13 }}>
      <Row label="Subtotal" value={fmt(totals.subtotal)} />
      {totals.itemDiscount > 0 && <Row label="Item Discount" value={`- ${fmt(totals.itemDiscount)}`} />}
      {totals.overallDisc > 0 && <Row label={`Overall Discount (${charges.discountPercent}%)`} value={`- ${fmt(totals.overallDisc)}`} />}
      <Row label="Taxable Value" value={fmt(totals.afterOverall)} />
      {totals.cgst > 0 && <Row label="CGST" value={fmt(totals.cgst)} />}
      {totals.sgst > 0 && <Row label="SGST" value={fmt(totals.sgst)} />}
      {totals.shipping > 0 && <Row label="Shipping" value={fmt(totals.shipping)} />}
      {totals.installation > 0 && <Row label="Installation" value={fmt(totals.installation)} />}
      {charges.roundOff && Math.abs(totals.roundOff) > 0.001 && (
        <Row label="Round Off" value={totals.roundOff >= 0 ? `+ ${fmt(totals.roundOff)}` : `- ${fmt(Math.abs(totals.roundOff))}`} />
      )}
      <div style={{ borderTop: `2px solid ${accent}`, marginTop: 6, paddingTop: 6 }}>
        <Row
          label={<span style={{ color: accent, fontWeight: 800 }}>GRAND TOTAL</span>}
          value={<span style={{ color: accent, fontWeight: 800 }}>₹ {fmt(finalAmount)}</span>}
        />
      </div>
      {totals.advance > 0 && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#475569' }}>
          <Row label={`Advance (${charges.advancePercent}%)`} value={`₹ ${fmt(totals.advance)}`} />
          <Row label="Balance" value={`₹ ${fmt(balanceAfterAdvance)}`} />
        </div>
      )}
    </div>
  );

  const NotesBlock = () => (
    <>
      {notes && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontWeight: 700, color: accent, marginBottom: 4 }}>Notes</div>
          <div style={{ fontSize: 12, color: '#334155', whiteSpace: 'pre-wrap' }}>{notes}</div>
        </div>
      )}
      {terms && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 700, color: accent, marginBottom: 4 }}>Terms & Conditions</div>
          <div style={{ fontSize: 12, color: '#334155', whiteSpace: 'pre-wrap' }}>{terms}</div>
        </div>
      )}
      {(bank.accountNo || bank.upi) && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 700, color: accent, marginBottom: 4 }}>Payment Details</div>
          <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6 }}>
            {bank.accountName && <>A/c Name: {bank.accountName}<br /></>}
            {bank.accountNo && <>A/c No: {bank.accountNo}<br /></>}
            {bank.bank && <>Bank: {bank.bank}<br /></>}
            {bank.ifsc && <>IFSC: {bank.ifsc}<br /></>}
            {bank.upi && <>UPI: {bank.upi}</>}
          </div>
        </div>
      )}
    </>
  );

  // 1. CLASSIC
  if (template === 'classic') {
    return (
      <div style={{ ...previewBase, padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            {logo && <LogoImg logo={logo} style={{ height: 56, width: 56 }} />}
            <div>
              <div style={{ fontWeight: 800, fontSize: 18, color: accent }}>{company.name || 'Company'}</div>
              <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5, marginTop: 2 }}>
                {company.address}
                {company.phone && <><br />{[company.phone, company.email, company.website].filter(Boolean).join(' · ')}</>}
                {company.gstin && <><br />GSTIN: {company.gstin}{company.pan && `   PAN: ${company.pan}`}</>}
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: accent }}>QUOTATION</div>
            <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
              No: <strong>{meta.quoteNo}</strong><br />
              Date: {meta.issueDate}<br />
              Valid Until: {meta.validUntil}<br />
              Status: <strong>{meta.status}</strong>
            </div>
          </div>
        </div>
        <div style={{ borderTop: `2px solid ${accent}`, margin: '12px 0' }} />
        <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, color: accent, fontSize: 12 }}>Quotation For</div>
            <div style={{ fontWeight: 700, fontSize: 13, marginTop: 2 }}>{client.name || '—'}</div>
            <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>
              {client.contactPerson && <>Attn: {client.contactPerson}<br /></>}
              {client.address && <>{client.address}<br /></>}
              {client.phone && <>Tel: {client.phone}<br /></>}
              {client.email && <>Email: {client.email}<br /></>}
              {client.gstin && <>GSTIN: {client.gstin}</>}
            </div>
          </div>
          {meta.subject && (
            <div>
              <div style={{ fontWeight: 700, color: accent, fontSize: 12 }}>Subject</div>
              <div style={{ fontSize: 12, color: '#1f2937', marginTop: 2 }}>{meta.subject}</div>
              {meta.reference && <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>Ref: {meta.reference}</div>}
            </div>
          )}
        </div>
        <div style={{ marginTop: 14 }}><ItemsTable /></div>
        <TotalsBlock />
        <NotesBlock />
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ borderTop: '1px solid #94a3b8', paddingTop: 4, width: 180, textAlign: 'center', fontSize: 11 }}>
            <strong>For {company.name || 'Company'}</strong>
            {meta.preparedBy && <div style={{ marginTop: 2 }}>Prepared by: {meta.preparedBy}</div>}
          </div>
        </div>
      </div>
    );
  }

  // 2. MODERN SIDEBAR
  if (template === 'modern') {
    return (
      <div style={{ ...previewBase, display: 'flex', minHeight: 500 }}>
        <div style={{ background: accent, color: '#fff', padding: 16, width: 170, flexShrink: 0 }}>
          {logo && (
            <div style={{ background: '#fff', borderRadius: 6, padding: 4, textAlign: 'center', marginBottom: 10 }}>
              <LogoImg logo={logo} style={{ height: 56 }} />
            </div>
          )}
          <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>{company.name || 'Company'}</div>
          <div style={{ fontSize: 10, opacity: 0.9, marginTop: 6, lineHeight: 1.5 }}>
            {company.address}
            {company.phone && <><br />{company.phone}</>}
            {company.email && <><br />{company.email}</>}
            {company.website && <><br />{company.website}</>}
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.4)', margin: '10px 0' }} />
          {company.gstin && (
            <div style={{ fontSize: 10 }}>
              <div style={{ opacity: 0.85, fontWeight: 700 }}>GSTIN</div>
              <div>{company.gstin}</div>
            </div>
          )}
          {company.pan && (
            <div style={{ fontSize: 10, marginTop: 6 }}>
              <div style={{ opacity: 0.85, fontWeight: 700 }}>PAN</div>
              <div>{company.pan}</div>
            </div>
          )}
        </div>
        <div style={{ flex: 1, padding: 20 }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: accent }}>QUOTATION</div>
          <div style={{ fontSize: 11, color: '#64748b' }}>#{meta.quoteNo}  ·  {meta.status}</div>
          <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: 8, fontSize: 12, marginTop: 14 }}>
            <div>
              <div style={{ fontWeight: 700 }}>To</div>
              <div style={{ fontWeight: 700 }}>{client.name || '—'}</div>
              {client.contactPerson && <div style={{ fontSize: 11 }}>Attn: {client.contactPerson}</div>}
              {client.address && <div style={{ fontSize: 11, color: '#64748b' }}>{client.address}</div>}
              {client.gstin && <div style={{ fontSize: 11, color: '#64748b' }}>GSTIN: {client.gstin}</div>}
            </div>
            <div>
              <div><strong>Issue Date:</strong> {meta.issueDate}</div>
              <div><strong>Valid Until:</strong> {meta.validUntil}</div>
              {meta.reference && <div><strong>Ref:</strong> {meta.reference}</div>}
              {meta.preparedBy && <div><strong>By:</strong> {meta.preparedBy}</div>}
            </div>
          </div>
          {meta.subject && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 700, color: accent, fontSize: 12 }}>Subject</div>
              <div style={{ fontSize: 12 }}>{meta.subject}</div>
            </div>
          )}
          <div style={{ marginTop: 14 }}><ItemsTable /></div>
          <TotalsBlock />
          <NotesBlock />
        </div>
      </div>
    );
  }

  // 3. MINIMAL
  if (template === 'minimal') {
    return (
      <div style={{ ...previewBase, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {logo && <LogoImg logo={logo} style={{ height: 44, width: 44 }} />}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{company.name || 'Company'}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              {[company.address, company.phone, company.email].filter(Boolean).join('  ·  ')}
              {company.gstin && <> · GSTIN {company.gstin}</>}
            </div>
          </div>
        </div>
        <div style={{ borderTop: '2px solid #0f172a', margin: '12px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>QUOTATION</div>
          <div style={{ fontSize: 12 }}>{meta.quoteNo}</div>
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
          Date: {meta.issueDate}  ·  Valid Until: {meta.validUntil}  ·  Status: {meta.status}
        </div>
        <div style={{ marginTop: 12, fontSize: 12 }}>
          <strong>To:</strong> {client.name || '—'}<br />
          {client.address && <span style={{ color: '#64748b' }}>{client.address}<br /></span>}
          {(client.phone || client.email) && <span style={{ color: '#64748b' }}>{[client.phone, client.email].filter(Boolean).join('  ·  ')}<br /></span>}
          {client.gstin && <span style={{ color: '#64748b' }}>GSTIN: {client.gstin}</span>}
        </div>
        {meta.subject && (
          <div style={{ marginTop: 10, fontSize: 12 }}>
            <strong>Re:</strong> {meta.subject}
          </div>
        )}
        <div style={{ marginTop: 14 }}><ItemsTable /></div>
        <TotalsBlock />
        <NotesBlock />
      </div>
    );
  }

  // 4. BANNER
  if (template === 'banner') {
    return (
      <div style={{ ...previewBase }}>
        <div style={{ background: accent, color: '#fff', padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          {logo && (
            <div style={{ background: '#fff', borderRadius: 6, padding: 4 }}>
              <LogoImg logo={logo} style={{ height: 56, width: 56 }} />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 20 }}>{company.name || 'Company'}</div>
            <div style={{ fontSize: 11, opacity: 0.95 }}>QUOTATION</div>
            <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
              {[company.address, company.phone, company.email].filter(Boolean).join('  ·  ')}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 11 }}>
            <div>#{meta.quoteNo}</div>
            <div>{meta.issueDate}</div>
            <div>Valid: {meta.validUntil}</div>
            <div style={{ marginTop: 4, fontWeight: 700 }}>{meta.status}</div>
          </div>
        </div>
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: 8, fontSize: 12 }}>
          <div>
            <div style={{ fontWeight: 700, color: accent }}>Quotation For</div>
            <div style={{ fontWeight: 700 }}>{client.name || '—'}</div>
            {client.contactPerson && <div style={{ fontSize: 11 }}>Attn: {client.contactPerson}</div>}
            {client.address && <div style={{ fontSize: 11, color: '#64748b' }}>{client.address}</div>}
            {client.gstin && <div style={{ fontSize: 11, color: '#64748b' }}>GSTIN: {client.gstin}</div>}
          </div>
          {meta.subject && (
            <div style={{ background: '#f1f5f9', padding: 8, borderRadius: 6 }}>
              <div style={{ fontWeight: 700, color: accent, fontSize: 11 }}>Subject</div>
              <div>{meta.subject}</div>
              {meta.reference && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Ref: {meta.reference}</div>}
            </div>
          )}
        </div>
        <div style={{ padding: '0 16px' }}><ItemsTable /></div>
        <div style={{ padding: '0 16px 16px' }}>
          <TotalsBlock />
          <NotesBlock />
        </div>
      </div>
    );
  }

  // 5. THERMAL
  return (
    <div style={{ ...previewBase, padding: 14, maxWidth: 320, margin: '12px auto', border: '1px dashed #cbd5e1' }}>
      {logo && (
        <div style={{ textAlign: 'center', marginBottom: 6 }}>
          <LogoImg logo={logo} style={{ height: 46 }} />
        </div>
      )}
      <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 15, color: accent }}>
        {company.name || 'Company'}
      </div>
      <div style={{ textAlign: 'center', fontSize: 11, color: '#475569', marginTop: 2, lineHeight: 1.4 }}>
        {company.address}
        {company.phone && <><br />{company.phone}</>}
        {company.gstin && <><br />GSTIN {company.gstin}</>}
      </div>
      <div style={{ borderTop: '1px dashed #94a3b8', margin: '10px 0' }} />
      <div style={{ textAlign: 'center', fontWeight: 700 }}>QUOTATION</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 6 }}>
        <span>#{meta.quoteNo}</span>
        <span>{meta.issueDate}</span>
      </div>
      <div style={{ fontSize: 11 }}>Valid Until: <strong>{meta.validUntil}</strong></div>
      {client.name && <div style={{ fontSize: 11, marginTop: 4 }}>To: <strong>{client.name}</strong></div>}
      {meta.subject && (
        <div style={{ fontSize: 11, fontStyle: 'italic', color: '#475569', marginTop: 4 }}>
          {meta.subject}
        </div>
      )}
      <div style={{ marginTop: 8 }}><ItemsTable compact /></div>
      <TotalsBlock />
      <div style={{ marginTop: 8, fontSize: 11, textAlign: 'center', color: '#64748b' }}>
        Status: <strong>{meta.status}</strong>
      </div>
      <div style={{ marginTop: 4, textAlign: 'center', fontWeight: 700, color: accent }}>
        Thank you!
      </div>
    </div>
  );
};

export default QuotationGenerator;
